import { useEffect, useState } from 'react';
import { getSchedulesByClub } from '@/api/schedule/scheduleApi';
import { SCHEDULE_FIELDS } from '@/api/schedule/schedule-fields.config';
import { TournamentStatus } from '@/types/tournament.model';
import { logError } from '@/utils/logger';

export interface WeeklyGridSibling {
  id: string;
  name: string;
}

/**
 * Tournaments that share this LONG tournament's weekly grid, i.e. its siblings. The tournament detail
 * doesn't carry the grid, so it's looked up among the club's grids. Works before the fixture exists,
 * unlike the round's siblingRounds, which need the week block.
 */
export function useWeeklyGridSiblings(tournamentId: string, clubId: string | undefined): WeeklyGridSibling[] {
  const [siblings, setSiblings] = useState<WeeklyGridSibling[]>([]);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    getSchedulesByClub(clubId, SCHEDULE_FIELDS.WITH_TOURNAMENTS)
      .then((schedules) => {
        if (cancelled) return;
        const grid = schedules.find(
          (s) => s.isWeeklyGrid && (s.tournaments ?? []).some((t) => t.id === tournamentId),
        );
        setSiblings(
          (grid?.tournaments ?? [])
            .filter((t) => t.id !== tournamentId && t.status !== TournamentStatus.CANCELLED)
            .map((t) => ({ id: t.id, name: t.name ?? 'Torneo sin nombre' })),
        );
      })
      // Only costs the siblings line, so it doesn't surface as an error.
      .catch((err) => logError('useWeeklyGridSiblings', err));
    return () => {
      cancelled = true;
    };
  }, [tournamentId, clubId]);

  return siblings;
}
